import { useState, useCallback } from 'react';
import { useAppStore } from '../../state/store';
import { functionRegistry } from '../../utils/functionRegistry';
import { getEmittedFields, getDefaultFieldForIndicator } from '../../utils/indicatorDisplayRegistry';
import { v4 as uuidv4 } from 'uuid';
import type { ExpressionNode, ExpressionGraph, Instrument, Indicator } from '../../state/types';

export interface SeriesConfigDialogSetters {
  setDialogSeriesLabel: (label: string) => void;
  setDialogIsOHLC: (isOHLC: boolean) => void;
  setDialogTargetSlotId: (id: string | null) => void;
  setDialogSeriesId: (id: string | null) => void;
  setDialogOpen: (open: boolean) => void;
  setDialogFunctionId: (id: string | null) => void;
  setDialogExistingTypes: (types: string[]) => void;
  setDialogExpectedType: (type: 'number' | 'string' | null) => void;
  setCompactDialog: (value: boolean) => void;
  setDialogAvailableFields: (fields: string[]) => void;
  setDialogDefaultField: (field: string | null) => void;
}

export interface UseSeriesConfigDialogReturn extends SeriesConfigDialogSetters {
  dialogOpen: boolean;
  dialogSeriesLabel: string;
  dialogIsOHLC: boolean;
  compactDialog: boolean;
  dialogFunctionId: string | null;
  dialogExistingTypes: string[];
  dialogExpectedType: 'number' | 'string' | null;
  dialogTargetSlotId: string | null;
  dialogSeriesId: string | null;
  dialogAvailableFields: string[];
  dialogDefaultField: string | null;
  seriesEditor: {
    expressionId: string;
    slotIndex: number;
    operandId: string;
  } | null;
  handleDialogCancel: () => void;
  handleDialogSubmit: (config: { field: string; offset: number }) => void;
  handleRequestEditSeries: (args: {
    expressionId: string;
    slotIndex: number;
    operandId: string;
  }) => void;
  handleCompactSeriesAwareSubmit: (config: { offset: number }) => void;
  expressionGraph: ExpressionGraph;
  setExpressionGraph: (
    graphOrUpdater: ExpressionGraph | ((prev: ExpressionGraph) => ExpressionGraph)
  ) => void;
}

export function useSeriesConfigDialog(graphKey: 'entry' | 'exit' = 'entry'): UseSeriesConfigDialogReturn {
  const entryGraph = useAppStore((s) => s.expressionGraph ?? { rootId: null, expressions: {} });
  const exitGraph = useAppStore((s) => s.exitExpressionGraph ?? { rootId: null, expressions: {} });
  const setEntryGraph = useAppStore((s) => s.setExpressionGraph);
  const setExitGraph = useAppStore((s) => s.setExitExpressionGraph);
  const expressionGraph = graphKey === 'exit' ? exitGraph : entryGraph;
  const setExpressionGraph = graphKey === 'exit' ? setExitGraph : setEntryGraph;
  const instruments = useAppStore((state) => state.instruments);
  const indicators = useAppStore((state) => state.indicators);

  const [dialogFunctionId, setDialogFunctionId] = useState<string | null>(null);
  const [dialogExistingTypes, setDialogExistingTypes] = useState<string[]>([]);
  const [dialogExpectedType, setDialogExpectedType] = useState<'number' | 'string' | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSeriesLabel, setDialogSeriesLabel] = useState('');
  const [dialogIsOHLC, setDialogIsOHLC] = useState(false);
  const [compactDialog, setCompactDialog] = useState(false);
  const [dialogTargetSlotId, setDialogTargetSlotId] = useState<string | null>(null);
  const [dialogSeriesId, setDialogSeriesId] = useState<string | null>(null);
  const [dialogAvailableFields, setDialogAvailableFields] = useState<string[]>([]);
  const [dialogDefaultField, setDialogDefaultField] = useState<string | null>(null);
  const [seriesEditor, setSeriesEditor] = useState<{
    expressionId: string;
    slotIndex: number;
    operandId: string;
  } | null>(null);

  const handleDialogCancel = useCallback(() => {
    setDialogOpen(false);
    setDialogTargetSlotId(null);
    setDialogSeriesId(null);
    setDialogAvailableFields([]);
    setDialogDefaultField(null);
    setSeriesEditor(null);
    setDialogFunctionId(null);
    setDialogExistingTypes([]);
    setDialogExpectedType(null);
  }, []);

  const handleDialogSubmit = useCallback(
    (config: { field: string; offset: number }) => {
      // === 1. Editing an existing series (pill clicked) ===
      if (seriesEditor) {
        setExpressionGraph((prev) => {
          const node = prev.expressions[seriesEditor.operandId];
          if (!node) return prev;

          if (node.type === 'ohlc_series' || node.type === 'indicator_series') {
            const updatedNode: ExpressionNode = {
              ...node,
              field: config.field as any,
              offset: config.offset,
              ...(node as any)._seriesAwareParamIndex !== undefined
                ? { _seriesAwareParamIndex: (node as any)._seriesAwareParamIndex }
                : {},
            };

            return {
              ...prev,
              expressions: {
                ...prev.expressions,
                [seriesEditor.operandId]: updatedNode,
              },
            };
          }
          return prev;
        });

        handleDialogCancel();
        return;
      }

      // === 2. Handle seriesAware drop (compact dialog) ===
      if (dialogTargetSlotId && dialogSeriesId) {
        const match = dialogTargetSlotId.match(/^(.+)-slot-(\d+)$/);
        if (match) {
          const parentId = match[1];
          const slotIndex = parseInt(match[2], 10);
          const parentNode = expressionGraph.expressions[parentId];

          if (parentNode?.type === 'function') {
            const def = functionRegistry.get(parentNode.functionId);
            const param = def?.parameters[slotIndex];

            if (param?.type === 'seriesAware' || param?.type === 'seriesAwareWindow') {
              const newId = `sa-${uuidv4()}`;
              const newNode: ExpressionNode = {
                id: newId,
                type: 'seriesAware',
                seriesId: dialogSeriesId,
                offset: config.offset ?? 0,
                requiredFields: param.requiredFields,
              };

              setExpressionGraph((prev) => {
                const parent = prev.expressions[parentId];
                if (!parent || parent.type !== 'function') return prev;

                const newOperandIds = [...parent.operandIds];
                while (newOperandIds.length <= slotIndex) newOperandIds.push(null);
                newOperandIds[slotIndex] = newId;

                return {
                  ...prev,
                  expressions: {
                    ...prev.expressions,
                    [newId]: newNode,
                    [parentId]: { ...parent, operandIds: newOperandIds },
                  },
                };
              });

              handleDialogCancel();
              return;
            }
          }
        }
      }

      // === 3. Regular series drop (full dialog) ===
      if (!dialogTargetSlotId || !dialogSeriesId) return;

      const slotMatch = dialogTargetSlotId.match(/^(.+)-slot-(\d+)$/);
      if (!slotMatch) return;

      const parentId = slotMatch[1];
      const operandIndex = parseInt(slotMatch[2], 10);
      const newId = `series-${uuidv4()}`;

      const isInstrument = instruments.some((inst: Instrument) => inst.id === dialogSeriesId);
      const isIndicator = indicators.some((ind: Indicator) => ind.id === dialogSeriesId);

      if (!isInstrument && !isIndicator) {
        console.warn(`[ExpressionEditor] Invalid seriesId: ${dialogSeriesId}`);
        return;
      }

      const baseNode = isInstrument
        ? {
            id: newId,
            type: 'ohlc_series' as const,
            seriesId: dialogSeriesId,
            field: config.field as 'open' | 'high' | 'low' | 'close' | 'volume',
            offset: config.offset,
          }
        : {
            id: newId,
            type: 'indicator_series' as const,
            seriesId: dialogSeriesId,
            offset: config.offset,
            field: config.field,
          };

      const insertSeriesIntoParent = (prev: ExpressionGraph): ExpressionGraph => {
        const parent = prev.expressions[parentId];
        if (!parent || parent.type !== 'function') return prev;

        const newOperandIds = [...parent.operandIds];
        if (operandIndex >= newOperandIds.length) {
          while (newOperandIds.length < operandIndex) newOperandIds.push(null);
          newOperandIds.push(newId);
        } else {
          newOperandIds[operandIndex] = newId;
        }

        const updatedParent: ExpressionNode = {
          ...parent,
          operandIds: newOperandIds,
        };

        return {
          ...prev,
          expressions: {
            ...prev.expressions,
            [newId]: baseNode,
            [parentId]: updatedParent,
          },
        };
      };

      setExpressionGraph(insertSeriesIntoParent);
      handleDialogCancel();
    },
    [
      seriesEditor,
      dialogTargetSlotId,
      dialogSeriesId,
      expressionGraph,
      instruments,
      indicators,
      setExpressionGraph,
      handleDialogCancel,
    ]
  );

  const handleRequestEditSeries = useCallback(
    ({
      expressionId,
      slotIndex,
      operandId,
    }: {
      expressionId: string;
      slotIndex: number;
      operandId: string;
    }) => {
      const node = expressionGraph.expressions[operandId];
      if (!node) return;

      setSeriesEditor({ expressionId, slotIndex, operandId });

      const parentNode = Object.values(expressionGraph.expressions).find(
        (n: ExpressionNode) =>
          n.type === 'function' && 'operandIds' in n && n.operandIds.includes(operandId)
      );

      const parentDef =
        parentNode && parentNode.type === 'function'
          ? functionRegistry.get(parentNode.functionId)
          : null;

      const paramIndex =
        parentNode && parentNode.type === 'function'
          ? parentNode.operandIds.indexOf(operandId)
          : -1;
      const param = parentDef?.parameters[paramIndex ?? -1];
      const isSeriesAwareSlot = param?.type === 'seriesAware' || param?.type === 'seriesAwareWindow';

      setCompactDialog(!!isSeriesAwareSlot);

      if (param?.type === 'seriesDate') {
        setDialogExpectedType('string');
      } else if (param?.type === 'series') {
        setDialogExpectedType(parentDef?.returnType === 'string' ? 'string' : 'number');
      } else {
        setDialogExpectedType(null);
      }

      if (node.type === 'ohlc_series') {
        const instrument = instruments.find((x) => x.id === node.seriesId);
        const symbol = instrument?.symbol ?? 'Unknown';
        setDialogSeriesLabel(`${symbol}`);
        setDialogIsOHLC(true);
        setDialogSeriesId(node.seriesId);
        setDialogAvailableFields([]);
        setDialogDefaultField(null);
        setDialogTargetSlotId(`${expressionId}-slot-${slotIndex}`);
      } else if (node.type === 'indicator_series') {
        const indicator = indicators.find((x) => x.id === node.seriesId);
        const instrument = instruments.find((x) => x.id === indicator?.instrumentId);
        const label = indicator
          ? indicator.name || `${instrument?.symbol ?? 'Unknown'} ${indicator.type}`
          : 'Unknown Indicator';
        setDialogSeriesLabel(label);
        setDialogIsOHLC(false);
        setDialogSeriesId(node.seriesId);
        setDialogAvailableFields(indicator ? Object.keys(getEmittedFields(indicator.type)) : []);
        setDialogDefaultField(indicator ? getDefaultFieldForIndicator(indicator.type) ?? null : null);
        setDialogTargetSlotId(`${expressionId}-slot-${slotIndex}`);
      }

      setDialogOpen(true);
    },
    [expressionGraph, instruments, indicators]
  );

  const handleCompactSeriesAwareSubmit = useCallback(
    (config: { offset: number }) => {
      if (!seriesEditor) return;

      const operandId = seriesEditor.operandId;
      const parentNode = Object.values(expressionGraph.expressions).find(
        (n): n is ExpressionNode & { type: 'function' } =>
          n.type === 'function' && n.operandIds.includes(operandId)
      );

      if (parentNode) {
        const slotIndex = parentNode.operandIds.indexOf(operandId);
        const def = functionRegistry.get(parentNode.functionId);
        const param = def?.parameters[slotIndex];
        const requiredFieldsCount =
          param?.type === 'seriesAware' || param?.type === 'seriesAwareWindow'
            ? param.requiredFields?.length ?? 0
            : 0;

        setExpressionGraph((prev) => {
          const newExprs = { ...prev.expressions };
          for (let i = 0; i < requiredFieldsCount; i++) {
            const id = parentNode.operandIds[slotIndex + i];
            if (id && newExprs[id]) {
              const existing = newExprs[id];
              if ('offset' in existing) {
                newExprs[id] = { ...existing, offset: config.offset } as typeof existing;
              }
            }
          }
          return { ...prev, expressions: newExprs };
        });
      }
    },
    [seriesEditor, expressionGraph, setExpressionGraph]
  );

  return {
    dialogOpen,
    dialogSeriesLabel,
    dialogIsOHLC,
    compactDialog,
    dialogFunctionId,
    dialogExistingTypes,
    dialogExpectedType,
    dialogTargetSlotId,
    dialogSeriesId,
    dialogAvailableFields,
    dialogDefaultField,
    seriesEditor,
    setDialogSeriesLabel,
    setDialogIsOHLC,
    setDialogTargetSlotId,
    setDialogSeriesId,
    setDialogOpen,
    setDialogFunctionId,
    setDialogExistingTypes,
    setDialogExpectedType,
    setCompactDialog,
    setDialogAvailableFields,
    setDialogDefaultField,
    handleDialogCancel,
    handleDialogSubmit,
    handleRequestEditSeries,
    handleCompactSeriesAwareSubmit,
    expressionGraph,
    setExpressionGraph,
  };
}
